import React, { useEffect, useRef, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { toast } from 'sonner';
import { waitlistAPI } from '../services/api';
import {
  ChartLineUp, Receipt, ArrowsOutSimple, UsersThree, ShieldCheck, CircleNotch, CheckCircle,
  Lock, Sparkle, MusicNotes, Bank, CalendarBlank, Lightning, Star, Heart, ArrowUpRight, CaretDown
} from '@phosphor-icons/react';

const GREEN = '#62CF5A';
const HEAD = { fontFamily: "'Barlow Condensed', sans-serif" };

const CATEGORIES = [
  { value: 'creator', label: 'Creator / Artist' },
  { value: 'business', label: 'Small Business Owner' },
  { value: 'agency', label: 'Agency / Manager' },
  { value: 'other', label: 'Other' },
];

function Reveal({ children, delay = 0, className = '' }) {
  const ref = useRef(null);
  const [shown, setShown] = useState(false);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const obs = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) { setShown(true); obs.disconnect(); } },
      { threshold: 0.12 }
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, []);
  return (
    <div ref={ref} className={`reveal ${shown ? 'reveal-in' : ''} ${className}`} style={{ '--reveal-delay': `${delay}ms` }}>
      {children}
    </div>
  );
}

/* ---------- Hero dashboard mockup ---------- */
function AreaChart() {
  return (
    <svg viewBox="0 0 400 110" className="w-full h-24" preserveAspectRatio="none">
      <defs>
        <linearGradient id="areaFill" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor={GREEN} stopOpacity="0.35" />
          <stop offset="100%" stopColor={GREEN} stopOpacity="0" />
        </linearGradient>
      </defs>
      <path d="M0,90 C30,80 45,60 70,64 C95,68 110,48 135,52 C160,56 175,40 200,46 C225,52 240,30 265,34 C290,38 305,22 330,26 C355,30 375,12 400,8 L400,110 L0,110 Z" fill="url(#areaFill)" />
      <path d="M0,90 C30,80 45,60 70,64 C95,68 110,48 135,52 C160,56 175,40 200,46 C225,52 240,30 265,34 C290,38 305,22 330,26 C355,30 375,12 400,8" fill="none" stroke={GREEN} strokeWidth="2.5" />
    </svg>
  );
}

const HERO_STATS = [
  { label: 'Invoices Sent', value: '152', delta: '23%' },
  { label: 'Paid Invoices', value: '136', delta: '19%' },
  { label: 'Total Paid Out', value: '$42,530.00', delta: '16%' },
  { label: 'Active Clients', value: '84', delta: '11%' },
];

const ACTIVITY = [
  { icon: Receipt, color: '#3B82F6', text: 'Invoice #INV-1034 paid by Spotify', amount: '$2,500.00', time: '2m ago' },
  { icon: UsersThree, color: GREEN, text: 'Payout to John Smith', amount: '$750.00', time: '19m ago' },
  { icon: Receipt, color: '#3B82F6', text: 'Invoice #INV-1023 paid by Apple Music', amount: '$1,250.00', time: '1h ago' },
  { icon: UsersThree, color: '#A855F7', text: 'Payout to Sarah Johnson', amount: '$600.00', time: '2h ago' },
];

const SOURCES = [
  { label: 'Streaming', pct: 42, color: GREEN },
  { label: 'Licensing', pct: 28, color: '#8B5CF6' },
  { label: 'Sync', pct: 18, color: '#FBBF24' },
  { label: 'Other', pct: 12, color: '#3B82F6' },
];

function HeroDashboard() {
  return (
    <div className="space-y-3 select-none" data-testid="hero-dashboard-mockup" aria-hidden="true">
      <div className="bg-[#0b0f0b] border border-[#1d251d] rounded-2xl p-5">
        <div className="flex items-start justify-between mb-1">
          <div>
            <p className="text-[11px] text-[#8a8a8a]">Total Revenue</p>
            <p className="text-2xl font-bold text-white" style={HEAD}>$128,540.75</p>
            <p className="text-[11px] mt-0.5"><span style={{ color: GREEN }}>▲ 18.6%</span> <span className="text-[#666]">vs last 30 days</span></p>
          </div>
          <span className="text-[10px] text-[#888] border border-[#262626] rounded-md px-2 py-1 flex items-center gap-1">Last 30 Days <CaretDown className="w-2.5 h-2.5" /></span>
        </div>
        <AreaChart />
        <div className="flex justify-between text-[9px] text-[#555] mt-1">
          {['May 1', 'May 8', 'May 15', 'May 22', 'May 29'].map((d) => <span key={d}>{d}</span>)}
        </div>
      </div>
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        {HERO_STATS.map((s) => (
          <div key={s.label} className="bg-[#0b0f0b] border border-[#1d251d] rounded-xl p-3">
            <p className="text-[9px] text-[#8a8a8a] leading-tight">{s.label}</p>
            <p className="text-sm font-bold text-white mt-1" style={HEAD}>{s.value}</p>
            <p className="text-[9px] mt-0.5" style={{ color: GREEN }}>▲ {s.delta}</p>
          </div>
        ))}
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <div className="bg-[#0b0f0b] border border-[#1d251d] rounded-xl p-4">
          <p className="text-[11px] text-[#8a8a8a] mb-3">Recent Activity</p>
          <div className="space-y-2.5">
            {ACTIVITY.map((a) => (
              <div key={a.text} className="flex items-center gap-2">
                <span className="w-5 h-5 rounded-full flex items-center justify-center shrink-0" style={{ background: `${a.color}20` }}>
                  <a.icon className="w-2.5 h-2.5" style={{ color: a.color }} />
                </span>
                <span className="text-[10px] text-[#bbb] truncate flex-1">{a.text}</span>
                <span className="text-[10px] text-white font-semibold">{a.amount}</span>
                <span className="text-[9px] text-[#555] hidden sm:inline">{a.time}</span>
              </div>
            ))}
          </div>
          <p className="text-[10px] text-[#777] mt-3 border border-[#262626] rounded-md px-2 py-1 inline-flex items-center gap-1">View All Activity <CaretDown className="w-2.5 h-2.5" /></p>
        </div>
        <div className="bg-[#0b0f0b] border border-[#1d251d] rounded-xl p-4">
          <p className="text-[11px] text-[#8a8a8a] mb-3">Revenue by Source</p>
          <div className="flex items-center gap-4">
            <div className="relative w-24 h-24 shrink-0 rounded-full" style={{ background: `conic-gradient(${GREEN} 0% 42%, #8B5CF6 42% 70%, #FBBF24 70% 88%, #3B82F6 88% 100%)` }}>
              <div className="absolute inset-[22%] rounded-full bg-[#0b0f0b]" />
            </div>
            <div className="space-y-1.5 flex-1">
              {SOURCES.map((s) => (
                <div key={s.label} className="flex items-center gap-1.5 text-[10px]">
                  <span className="w-2 h-2 rounded-[2px]" style={{ background: s.color }} />
                  <span className="text-[#bbb] flex-1">{s.label}</span>
                  <span className="text-white font-semibold">{s.pct}%</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

/* ---------- Feature mini-visuals ---------- */
function MiniBars({ color = GREEN, heights }) {
  return (
    <div className="flex items-end gap-[3px] h-16">
      {heights.map((h, i) => <div key={`bar-${i}`} className="flex-1 rounded-sm" style={{ height: `${h}%`, background: color, opacity: 0.85 }} />)}
    </div>
  );
}

function MiniDonut() {
  return (
    <div className="flex items-center gap-4">
      <div className="relative w-20 h-20 shrink-0 rounded-full" style={{ background: `conic-gradient(${GREEN} 0% 84%, #FBBF24 84% 92%, #EF4444 92% 100%)` }}>
        <div className="absolute inset-[18%] rounded-full bg-[#0b0f0b] flex flex-col items-center justify-center">
          <span className="text-sm font-bold text-white leading-none" style={HEAD}>152</span>
          <span className="text-[8px] text-[#888]">Sent</span>
        </div>
      </div>
      <div className="space-y-1 text-[10px]">
        {[['Paid', '128 (84%)', GREEN], ['Pending', '12 (8%)', '#FBBF24'], ['Overdue', '4 (2%)', '#EF4444']].map(([l, v, c]) => (
          <div key={l} className="flex items-center gap-1.5"><span className="w-2 h-2 rounded-[2px]" style={{ background: c }} /><span className="text-[#bbb]">{l}</span><span className="text-[#666]">{v}</span></div>
        ))}
      </div>
    </div>
  );
}

function MiniLine() {
  return (
    <svg viewBox="0 0 160 60" className="w-full h-16" preserveAspectRatio="none">
      <path d="M0,52 C15,50 25,44 40,45 C55,46 65,34 80,36 C95,38 105,24 120,22 C135,20 148,10 160,6" fill="none" stroke="#A855F7" strokeWidth="2.5" />
      <circle cx="120" cy="22" r="3" fill="#A855F7" />
    </svg>
  );
}

const FEATURES = [
  {
    id: 'feat-dashboard', icon: ChartLineUp, title: 'Real-time revenue dashboard',
    desc: 'See every dollar coming in, from every client, in one place.',
    visual: <div><p className="text-[10px] text-[#8a8a8a] mb-2">Revenue Overview <span className="text-[#555]">· Last 30 Days</span></p><MiniBars heights={[35, 55, 40, 70, 50, 65, 45, 80, 60, 75, 55, 90, 70, 85]} /></div>,
  },
  {
    id: 'feat-invoicing', icon: Receipt, title: 'Invoicing in under 60 seconds',
    desc: 'Professional invoices, sent and tracked without leaving the app.',
    visual: <div><p className="text-[10px] text-[#8a8a8a] mb-2">Invoices</p><MiniDonut /></div>,
  },
  {
    id: 'feat-splits', icon: ArrowsOutSimple, title: 'Auto-split payments',
    desc: 'Pay your engineer, your featured artist, or your collaborator automatically, the moment a client pays you.',
    visual: <div><p className="text-[10px] text-[#8a8a8a] mb-1">Payouts This Month</p><p className="text-sm font-bold text-white mb-2" style={HEAD}>$42,530.00</p><MiniBars heights={[45, 60, 35, 75, 55, 85, 50, 70, 60, 90, 65, 80]} /></div>,
  },
  {
    id: 'feat-support', icon: UsersThree, title: 'Support pages',
    desc: 'Let fans and clients back your next project with a simple, direct payment link.',
    visual: <div><p className="text-[10px] text-[#8a8a8a] mb-1">Support Page</p><p className="text-sm font-bold text-white" style={HEAD}>$3,210.00</p><p className="text-[9px] text-[#666] mb-1">Total Raised</p><MiniLine /></div>,
  },
  {
    id: 'security', icon: ShieldCheck, title: 'Bank-level security',
    desc: 'Powered by Stripe, the same infrastructure trusted by millions of businesses.',
    visual: (
      <div>
        <p className="text-[10px] text-[#8a8a8a] mb-2">Security</p>
        <div className="flex flex-col items-center py-1">
          <div className="w-12 h-12 rounded-xl flex items-center justify-center mb-2" style={{ background: `${GREEN}15`, border: `1px solid ${GREEN}40` }}>
            <ShieldCheck className="w-6 h-6" style={{ color: GREEN }} weight="fill" />
          </div>
          {['256-bit encryption', 'Fraud protection', 'Secure & encrypted'].map((t) => <p key={t} className="text-[10px] text-[#bbb] leading-relaxed">{t}</p>)}
        </div>
      </div>
    ),
  },
];

const WHY_STATS = [
  { icon: UsersThree, big: '160K+', label: 'Audience Built', sub: 'All Bay Music Magazine' },
  { icon: Bank, big: '10+', label: 'Industry Partners', sub: 'Distribution Relationships' },
  { icon: CalendarBlank, big: 'Years', label: 'In the Game', sub: 'Music Industry Experience' },
];

const FAQS = [
  { q: 'When is Divispend launching?', a: 'We\u2019re launching on iOS and Android soon. Waitlist members get early access first, released in waves \u2014 you\u2019ll get an email with your invite the moment your wave opens.' },
  { q: 'Does Divispend hold my money?', a: 'No. All payments are processed and held by Stripe, the same payment infrastructure trusted by millions of businesses. Divispend is the dashboard on top \u2014 we never take custody of your funds.' },
  { q: 'Who is Divispend built for?', a: 'Creators, musicians, and small businesses who are tired of juggling five different payment tools \u2014 anyone who invoices clients, splits payments with collaborators, or takes support from fans.' },
  { q: 'What does it cost to join the waitlist?', a: 'Nothing. Joining the waitlist is free, and early-access members will be the first to see pricing before anyone else.' },
];

const PERKS = [
  { icon: Lightning, text: 'Early access before anyone else' },
  { icon: Star, text: 'Exclusive updates and new features' },
  { icon: Heart, text: 'Help shape the future of Divispend' },
];

export default function WaitlistLandingPage() {
  const [searchParams] = useSearchParams();
  const utmSource = searchParams.get('utm_source') || null;
  const [email, setEmail] = useState('');
  const [category, setCategory] = useState('');
  const [socialHandle, setSocialHandle] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [joined, setJoined] = useState(false);
  const [openFaq, setOpenFaq] = useState(0);
  const formRef = useRef(null);

  useEffect(() => { document.title = 'Divispend — One dashboard for your business\'s money'; }, []);

  const scrollToForm = () => formRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });

  const submit = async (e, fromHero = false) => {
    e.preventDefault();
    if (fromHero) { scrollToForm(); return; }
    if (!email.trim()) { toast.error('Please enter your email'); return; }
    setSubmitting(true);
    try {
      const { data } = await waitlistAPI.join({
        email: email.trim(),
        category: category || null,
        social_handle: socialHandle || null,
        utm_source: utmSource,
      });
      setJoined(true);
      toast.success(data.message);
    } catch (err) {
      toast.error(err?.response?.data?.detail?.[0]?.msg || err?.response?.data?.detail || 'Something went wrong — please try again');
    } finally {
      setSubmitting(false);
    }
  };

  const inputCls = "w-full bg-[#0d0d0d] border-2 border-[#262626] rounded-full px-6 h-14 text-white placeholder-[#555] focus:outline-none focus:border-[#62CF5A] transition-colors";

  return (
    <div className="min-h-screen bg-black text-white" data-testid="waitlist-landing-page" style={{ fontFamily: "'Barlow', sans-serif" }}>
      {/* Nav */}
      <header className="sticky top-0 z-40 bg-black/85 backdrop-blur-md border-b border-[#131313]">
        <div className="max-w-6xl mx-auto px-5 sm:px-6 h-20 flex items-center justify-between">
          <a href="/" className="flex items-center" data-testid="nav-logo">
            <img src="/brand/divispend-logo.png" alt="Divispend" className="h-12 sm:h-14 w-auto" />
          </a>
          <nav className="hidden md:flex items-center gap-7 text-sm text-[#b3b3b3]">
            <a href="#features" className="hover:text-white transition-colors" data-testid="nav-features">Features</a>
            <a href="#why" className="hover:text-white transition-colors" data-testid="nav-why">Why Divispend</a>
            <a href="#security" className="hover:text-white transition-colors" data-testid="nav-security">Security</a>
            <a href="#faq" className="hover:text-white transition-colors" data-testid="nav-faq">FAQ</a>
            <Link to="/login" className="hover:text-white transition-colors" data-testid="nav-signin-link">Sign in</Link>
          </nav>
          <div className="flex items-center gap-3">
            <Link to="/login" className="md:hidden text-sm text-[#b3b3b3] hover:text-white transition-colors" data-testid="nav-signin-link-mobile">Sign in</Link>
            <Link to="/join" className="hidden sm:block text-sm text-[#b3b3b3] hover:text-white transition-colors" data-testid="nav-redeem-link">Have an invite?</Link>
            <button onClick={scrollToForm} className="h-10 px-5 rounded-full font-bold text-black text-sm active:scale-[0.98] transition-transform" style={{ background: GREEN }} data-testid="nav-join-button">
              Join the Waitlist
            </button>
          </div>
        </div>
      </header>

      {/* Hero */}
      <section className="max-w-6xl mx-auto px-5 sm:px-6 pt-14 sm:pt-20 pb-16 grid lg:grid-cols-2 gap-12 items-center">
        <Reveal>
          <div className="inline-flex items-center gap-2 border border-[#262626] rounded-full px-4 py-1.5 mb-8 bg-[#0a0a0a]">
            <Sparkle className="w-3.5 h-3.5" style={{ color: GREEN }} weight="fill" />
            <span className="text-[11px] tracking-[0.15em] font-semibold"><span style={{ color: GREEN }}>BUILT FOR CREATORS.</span> <span className="text-[#999]">BACKED BY FINANCE.</span></span>
          </div>
          <h1 className="text-5xl sm:text-6xl leading-[0.95] mb-6" style={{ ...HEAD, fontWeight: 700 }} data-testid="hero-headline">
            One dashboard for your <span style={{ color: GREEN }}>business&rsquo;s money.</span>
          </h1>
          <p className="text-base sm:text-lg text-[#b3b3b3] leading-relaxed mb-8 max-w-xl">
            Divispend brings your revenue, invoices, and collaborator payouts into a single app —
            built for creators, musicians, and small businesses tired of juggling five different payment tools.
          </p>
          <form onSubmit={(e) => submit(e, true)} className="flex flex-col sm:flex-row gap-3 max-w-lg">
            <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="you@example.com" className={`flex-1 ${inputCls}`} data-testid="hero-email-input" />
            <button type="submit" className="h-14 px-7 rounded-full font-bold text-black text-base active:scale-[0.98] transition-transform whitespace-nowrap" style={{ background: GREEN }} data-testid="hero-join-button">
              Join the Waitlist
            </button>
          </form>
          <p className="text-sm text-[#777] mt-4" data-testid="hero-launch-line">Launching on iOS and Android — join the waitlist for early access.</p>
          <p className="text-sm text-[#777] mt-6 mb-3">Trusted by <span style={{ color: GREEN }}>creators</span>. Backed by Stripe.</p>
          <div className="inline-flex flex-wrap items-stretch border border-[#1d1d1d] rounded-xl divide-x divide-[#1d1d1d] bg-[#0a0a0a]">
            <div className="px-4 sm:px-5 py-3">
              <p className="text-[9px] text-[#666] mb-0.5">Powered by</p>
              <p className="text-lg font-bold text-white tracking-tight" style={{ fontFamily: "'Barlow', sans-serif" }}>stripe</p>
            </div>
            <div className="px-4 sm:px-5 py-3 flex items-center gap-2">
              <Lock className="w-4 h-4 text-[#999]" />
              <p className="text-xs text-[#999] leading-tight">Bank-level<br />security</p>
            </div>
            <div className="px-4 sm:px-5 py-3 flex items-center gap-2">
              <MusicNotes className="w-4 h-4 text-[#999]" />
              <p className="text-xs text-[#999] leading-tight">Built for<br />creators</p>
            </div>
          </div>
        </Reveal>
        <Reveal delay={150}>
          <HeroDashboard />
        </Reveal>
      </section>

      {/* Features */}
      <section id="features" className="border-t border-[#131313] py-20 scroll-mt-20">
        <div className="max-w-6xl mx-auto px-5 sm:px-6">
          <Reveal>
            <h2 className="text-3xl sm:text-4xl text-center mb-14" style={{ ...HEAD, fontWeight: 700 }} data-testid="features-headline">
              Everything you need. Nothing you don&rsquo;t.
            </h2>
          </Reveal>
          <div className="grid sm:grid-cols-2 lg:grid-cols-5 gap-6">
            {FEATURES.map((f, i) => (
              <Reveal key={f.id} delay={i * 90} className="flex">
                <div id={f.id} className="flex flex-col scroll-mt-24 w-full" data-testid={f.id}>
                <div className="w-12 h-12 rounded-xl flex items-center justify-center mb-4" style={{ background: `${GREEN}10`, border: `1px solid ${GREEN}35` }}>
                  <f.icon className="w-5 h-5" style={{ color: GREEN }} weight="duotone" />
                </div>
                <h3 className="font-bold text-white text-lg leading-tight mb-2" style={HEAD}>{f.title}</h3>
                <p className="text-xs text-[#999] leading-relaxed mb-4 flex-1">{f.desc}</p>
                <div className="bg-[#0b0f0b] border border-[#1d251d] rounded-xl p-3.5">{f.visual}</div>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* Why Divispend */}
      <section id="why" className="border-t border-[#131313] py-20 scroll-mt-20">
        <div className="max-w-6xl mx-auto px-5 sm:px-6 grid lg:grid-cols-2 gap-12 items-center">
          <Reveal>
            <div>
            <h2 className="text-3xl sm:text-4xl mb-5" style={{ ...HEAD, fontWeight: 700 }} data-testid="why-headline">Why Divispend?</h2>
            <p className="text-white leading-relaxed mb-4 text-base sm:text-lg" style={HEAD}>
              Built by a music industry executive with Bay Area roots — not another fintech team guessing what creators need.
            </p>
            <p className="text-[#999] leading-relaxed text-sm sm:text-base">
              Our founder grew a 160,000-strong audience through All Bay Music Magazine and has spent years inside
              the business of independent music, with distribution relationships with major players in the industry.
              Divispend exists because the tools artists and small businesses actually use every day — five different
              payment apps, none of them talking to each other — were built for someone else.
            </p>
            </div>
          </Reveal>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            {WHY_STATS.map((s, i) => (
              <Reveal key={s.label} delay={i * 100}>
                <div className="bg-[#0a0a0a] border border-[#1d1d1d] rounded-2xl p-6 text-center h-full" data-testid={`why-stat-${s.label.toLowerCase().replace(/\s+/g, '-')}`}>
                  <s.icon className="w-8 h-8 mx-auto mb-4" style={{ color: GREEN }} weight="duotone" />
                  <p className="text-3xl font-bold text-white mb-1" style={HEAD}>{s.big}</p>
                  <p className="text-sm text-white font-semibold">{s.label}</p>
                  <p className="text-[11px] text-[#777] mt-1">{s.sub}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* Waitlist form */}
      <section ref={formRef} className="border-t border-[#131313] py-20">
        <div className="max-w-2xl mx-auto px-5 sm:px-6">
          <Reveal>
          {joined ? (
            <div className="text-center py-8" data-testid="waitlist-success">
              <CheckCircle className="w-14 h-14 mx-auto mb-4" style={{ color: GREEN }} weight="fill" />
              <h2 className="text-3xl mb-2" style={{ ...HEAD, fontWeight: 700 }}>You&rsquo;re on the list.</h2>
              <p className="text-[#999]">Check your inbox — confirmation is on its way. We&rsquo;ll email your invite when your wave opens.</p>
            </div>
          ) : (
            <>
              <h2 className="text-3xl sm:text-4xl text-center mb-2" style={{ ...HEAD, fontWeight: 700 }} data-testid="form-headline">Get early access</h2>
              <p className="text-[#999] text-sm text-center mb-8">Three fields. That&rsquo;s it.</p>
              <form onSubmit={(e) => submit(e, false)} className="space-y-4" data-testid="waitlist-form">
                <input type="email" required value={email} onChange={(e) => setEmail(e.target.value)} placeholder="Email (required)" className={inputCls} data-testid="form-email-input" />
                <div className="grid sm:grid-cols-2 gap-4">
                  <select value={category} onChange={(e) => setCategory(e.target.value)} className={`${inputCls} appearance-none`} data-testid="form-category-select">
                    <option value="">What best describes you?</option>
                    {CATEGORIES.map((c) => <option key={c.value} value={c.value}>{c.label}</option>)}
                  </select>
                  <input type="text" value={socialHandle} onChange={(e) => setSocialHandle(e.target.value)} placeholder="Instagram / social handle (optional)" className={inputCls} data-testid="form-social-input" />
                </div>
                <button type="submit" disabled={submitting}
                  className="w-full h-14 rounded-full font-bold text-black text-base active:scale-[0.98] transition-transform disabled:opacity-60 flex items-center justify-center gap-2"
                  style={{ background: GREEN }} data-testid="form-submit-button">
                  {submitting ? <CircleNotch className="w-5 h-5 animate-spin" /> : null}
                  {submitting ? 'Joining…' : 'Join the Waitlist'}
                  {!submitting && <ArrowUpRight className="w-4 h-4" weight="bold" />}
                </button>
              </form>
              <div className="flex flex-col sm:flex-row items-center justify-center gap-4 sm:gap-8 mt-8">
                {PERKS.map((p) => (
                  <div key={p.text} className="flex items-center gap-2 text-xs text-[#999]">
                    <p.icon className="w-4 h-4" style={{ color: GREEN }} weight="duotone" />
                    {p.text}
                  </div>
                ))}
              </div>
            </>
          )}
          </Reveal>
        </div>
      </section>

      {/* FAQ */}
      <section id="faq" className="border-t border-[#131313] py-20 scroll-mt-20">
        <div className="max-w-2xl mx-auto px-5 sm:px-6">
          <Reveal>
            <h2 className="text-3xl sm:text-4xl text-center mb-10" style={{ ...HEAD, fontWeight: 700 }} data-testid="faq-headline">Questions, answered</h2>
          </Reveal>
          <div className="space-y-3">
            {FAQS.map((f, i) => (
              <Reveal key={f.q} delay={i * 80}>
                <div className="border border-[#1d1d1d] rounded-2xl bg-[#0a0a0a] overflow-hidden">
                <button onClick={() => setOpenFaq(openFaq === i ? -1 : i)} className="w-full flex items-center justify-between px-5 py-4 text-left" data-testid={`faq-toggle-${i}`}>
                  <span className="font-semibold text-white text-sm sm:text-base pr-4" style={HEAD}>{f.q}</span>
                  <CaretDown className={`w-4 h-4 text-[#666] shrink-0 transition-transform ${openFaq === i ? 'rotate-180' : ''}`} />
                </button>
                {openFaq === i && <p className="px-5 pb-4 text-sm text-[#999] leading-relaxed" data-testid={`faq-answer-${i}`}>{f.a}</p>}
              </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="border-t border-[#131313] py-10">
        <div className="max-w-6xl mx-auto px-5 sm:px-6 flex flex-col sm:flex-row items-center justify-between gap-4">
          <img src="/brand/divispend-logo.png" alt="Divispend" className="h-14 w-auto" data-testid="footer-logo" />
          <div className="flex items-center gap-6 text-sm text-[#777]">
            <Link to="/join" className="hover:text-white transition-colors" data-testid="footer-redeem-link">Redeem invite</Link>
            <Link to="/login" className="hover:text-white transition-colors" data-testid="footer-signin-link">Sign in</Link>
            <Link to="/privacy_policy" className="hover:text-white transition-colors" data-testid="footer-privacy-link">Privacy Policy</Link>
            <Link to="/terms" className="hover:text-white transition-colors" data-testid="footer-terms-link">Terms of Service</Link>
          </div>
          <p className="text-xs text-[#555]">© {new Date().getFullYear()} Divispend. Launching soon.</p>
        </div>
      </footer>
    </div>
  );
}
